/**
 * 案件区分（大項目）の選択肢
 */
export const CATEGORY_LARGE = [
    '買取・仕入',
    '不動産仲介',
    '建築受注（木造）',
    '建築受注（ＲＣ・マンション）',
    '改修工事',
    'その他',
] as const;

/**
 * 大項目ごとの中項目マッピング
 * 大項目を選択すると、対応する中項目のみが表示される
 */
export const CATEGORY_MEDIUM_BY_LARGE: Record<string, readonly string[]> = {
    '買取・仕入': ['土地売り', '建売・分譲', '投資物件', '中古物件（買取再販）'],
    '不動産仲介': ['不動産仲介', '業務委託'],
    '建築受注（木造）': ['注文住宅', 'アパート', '戸建賃貸・テラスハウス', '宿泊施設', '非住宅'],
    '建築受注（ＲＣ・マンション）': ['マンション', 'ホテル', '非住宅'],
    '改修工事': ['大規模改修', '内装リフォーム', 'その他'],
    'その他': ['その他'],
};

/**
 * 案件フェーズの選択肢
 */
export const PHASE = [
    '1.見込み',
    '2.アポイント/買付提出',
    '3.プレゼン/条件摺合わせ',
    '4.クロージング/売渡書取得',
    '5.内諾/売買契約',
    '6.受注(契約)/決済',
    '7.失注',
] as const;

/**
 * デフォルトで選択されるフェーズ（受注・失注を除く）
 */
export const DEFAULT_PHASES = PHASE.filter(p => p !== '6.受注(契約)/決済' && p !== '7.失注');

export type CategoryLarge = typeof CATEGORY_LARGE[number];
export type Phase = typeof PHASE[number];

/**
 * フェーズごとの色設定（成約確度の温度勾配 + 結果ステージは意味色）
 * Chip 表示で使用する。bg は淡い背景、fg は濃い文字色。
 */
export const PHASE_COLORS: Record<Phase, { bg: string; fg: string }> = {
    '1.見込み': { bg: '#eceff1', fg: '#455a64' }, // blueGrey: 着手前
    '2.アポイント/買付提出': { bg: '#e3f2fd', fg: '#1565c0' }, // blue: 接触開始
    '3.プレゼン/条件摺合わせ': { bg: '#e0f7fa', fg: '#00838f' }, // cyan: 商談中
    '4.クロージング/売渡書取得': { bg: '#fff8e1', fg: '#f57f17' }, // amber: 大詰め
    '5.内諾/売買契約': { bg: '#fff3e0', fg: '#e65100' }, // orange: 決定間近
    '6.受注(契約)/決済': { bg: '#e8f5e9', fg: '#2e7d32' }, // green: 成功
    '7.失注': { bg: '#ffebee', fg: '#c62828' }, // red: 失敗
};

/**
 * 案件種別（仕入案件 / 売上案件）の選択肢
 * DB には英語コード (purchase / sales) で保存し、NULL は未分類（手動整理待ち）を表す
 */
export const PROJECT_TYPE_OPTIONS = [
    { code: 'purchase', label: '仕入' },
    { code: 'sales', label: '売上' },
] as const;

export type ProjectTypeCode = typeof PROJECT_TYPE_OPTIONS[number]['code'];

export const PROJECT_TYPE_LABELS: Record<string, string> = {
    purchase: '仕入',
    sales: '売上',
};

/**
 * 案件種別ごとの色設定（Chip 表示用、PHASE_COLORS と同じ bg/fg 形式）
 * unclassified は NULL（未分類）の表示に使用する
 */
export const PROJECT_TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
    purchase: { bg: '#e8f5e9', fg: '#2e7d32' }, // green: 土地・仕入
    sales: { bg: '#e3f2fd', fg: '#1565c0' }, // blue: 建築・売上
    unclassified: { bg: '#fafafa', fg: '#9e9e9e' }, // grey: 未分類（整理待ち）
};

/**
 * 火災保険の固定契約内容（SFA 案件 基本情報タブの表示用）
 * 全社一律で支払先・金額とも固定のため DB には保存しない
 */
export const FIRE_INSURANCE_PAYEE = '三井住友海上';
export const FIRE_INSURANCE_AMOUNT = 50000;

/**
 * ナビゲーションメニューのカテゴリ（括り）
 * Firestore: HozenallCloud_Roles_Permissions
 * コレクション: sort_noの整数部分をカテゴリIDとして使用 (例: 2.1 -> "2")
 */
export const MENU_CATEGORIES: Record<string, string> = {
    '2': '営業支援システム',
    '3': 'サマリー',
    '6': '経理メニュー',
    '8': 'マスタ',
    '9': 'デベロップメント',
};
